import { useState, useEffect, useCallback, useRef } from "react";
import { useI18n } from "@/i18n";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import { listProducts } from "@/infrastructure/product/productService";
import type { UnitConversion, TypeUnit, Product, ProductMeta } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

const PRODUCT_PAGE_SIZE = 8;

export function ConversionFormModal({
  item,
  onSave,
  onClose,
}: {
  item: UnitConversion | null;
  onSave: (data: { fromUnitId: string; toUnitId: string; productId?: string; factor: number; description?: string }) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isEditing = !!item;
  const [step, setStep] = useState(isEditing ? 3 : 1);
  const [fromUnitId, setFromUnitId] = useState(item?.fromUnitId ?? "");
  const [toUnitId, setToUnitId] = useState(item?.toUnitId ?? "");
  const [productId, setProductId] = useState<string>(item?.productId ?? "");
  const [factor, setFactor] = useState(item ? String(item.factor) : "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [units, setUnits] = useState<TypeUnit[]>([]);

  useEffect(() => { listTypeUnits().then(all => setUnits(all.filter(u => u.status === "ACTIVE"))).catch(() => {}); }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const canNext1 = !!fromUnitId && !!toUnitId && fromUnitId !== toUnitId;
  const canSave = !!factor && Number(factor) > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave({ fromUnitId, toUnitId, productId: productId || undefined, factor: Number(factor), description: description.trim() || undefined });
  };

  const getUnitLabel = (id: string) => { const u = units.find(u => u.id === id); return u ? `${u.name} (${u.abbreviation})` : "—"; };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "500px", display: "flex", flexDirection: "column", gap: "20px", maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
            {isEditing ? t.unitConversion.editConversion : t.unitConversion.addConversion}
          </h2>
          {!isEditing && (
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>
              {step}/3
            </span>
          )}
        </div>

        {isEditing && (
          <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#aaa", display: "flex", flexDirection: "column", gap: "4px", background: "#f9f9f9", borderRadius: "10px", padding: "12px 14px" }}>
            <span>{t.unitConversion.fromUnitLabel}: <strong style={{ color: "#555" }}>{getUnitLabel(item.fromUnitId)}</strong></span>
            <span>{t.unitConversion.toUnitLabel}: <strong style={{ color: "#555" }}>{getUnitLabel(item.toUnitId)}</strong></span>
            <span>{t.unitConversion.productLabel}: <strong style={{ color: "#555" }}>{item.productId ? "—" : t.unitConversion.globalLabel}</strong></span>
          </div>
        )}

        {step === 1 && (
          <>
            <SelectField label={t.unitConversion.fromUnitLabel} value={fromUnitId} onChange={setFromUnitId} placeholder={t.unitConversion.unitPlaceholder} options={units.map(u => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }))} required />
            <SelectField label={t.unitConversion.toUnitLabel} value={toUnitId} onChange={setToUnitId} placeholder={t.unitConversion.unitPlaceholder} options={units.map(u => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }))} required />
            <ModalFooter onCancel={onClose} onNext={() => setStep(2)} nextDisabled={!canNext1} nextLabel="Next →" />
          </>
        )}

        {step === 2 && (
          <>
            <ProductPicker productId={productId} onSelect={setProductId} />
            <ModalFooter onCancel={onClose} onBack={() => setStep(1)} onNext={() => setStep(3)} nextLabel="Next →" />
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.unitConversion.factorLabel} <span style={{ color: colors.primary }}>*</span></label>
              <input value={factor} onChange={e => setFactor(e.target.value)} placeholder={t.unitConversion.factorPlaceholder} type="number" min="0.01" step="any" style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.unitConversion.descriptionLabel}</label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder={t.unitConversion.descriptionPlaceholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }} />
            </div>
            <ModalFooter onCancel={onClose} onBack={isEditing ? undefined : () => setStep(2)} onSave={handleSave} saveDisabled={!canSave} saveLabel={t.unitConversion.save} />
          </>
        )}
      </div>
    </div>
  );
}

function ProductPicker({ productId, onSelect }: { productId: string; onSelect: (id: string) => void }) {
  const { t } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PRODUCT_PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await listProducts({ page, limit: PRODUCT_PAGE_SIZE, search: debouncedSearch || undefined });
      setProducts(res.items);
      setMeta(res.meta);
    } catch {}
  }, [page, debouncedSearch]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.unitConversion.productLabel}</span>
      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by code or name..."
        style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "9px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "240px", overflowY: "auto" }}>
        <RadioOption selected={productId === ""} onClick={() => onSelect("")} label={t.unitConversion.productPlaceholder} subtle />
        {products.map(p => (
          <RadioOption key={p.id} selected={productId === p.id} onClick={() => onSelect(p.id)} label={`${p.code} — ${p.name}`} />
        ))}
      </div>
      {meta.totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>
            {((meta.currentPage - 1) * meta.itemsPerPage) + 1}–{Math.min(meta.currentPage * meta.itemsPerPage, meta.totalItems)} of {meta.totalItems}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <MiniBtn label="‹" disabled={page === 1} onClick={() => setPage(p => p - 1)} />
            <MiniBtn label="›" disabled={page === meta.totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}
    </div>
  );
}

function RadioOption({ selected, onClick, label, subtle = false }: { selected: boolean; onClick: () => void; label: string; subtle?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: "10px", width: "100%", textAlign: "left",
        padding: "9px 12px", borderRadius: "8px", border: "none", cursor: "pointer",
        background: selected ? "#f0f7ff" : "transparent",
        transition: `background ${transitions.fast}`,
      }}
      onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = "#f9f9f9"; }}
      onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
    >
      <span style={{ width: "16px", height: "16px", borderRadius: "50%", border: `2px solid ${selected ? colors.primary : "#ddd"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {selected && <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: colors.primary }} />}
      </span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: subtle ? "#888" : "#333", fontStyle: subtle ? "italic" : "normal", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
    </button>
  );
}

function ModalFooter({ onCancel, onBack, onNext, onSave, nextDisabled = false, saveDisabled = false, nextLabel, saveLabel }: {
  onCancel: () => void; onBack?: () => void; onNext?: () => void; onSave?: () => void;
  nextDisabled?: boolean; saveDisabled?: boolean; nextLabel?: string; saveLabel?: string;
}) {
  return (
    <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
      {onBack && <button type="button" onClick={onBack} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer", marginRight: "auto" }}>← Back</button>}
      <button type="button" onClick={onCancel} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>Cancel</button>
      {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: nextDisabled ? "#ccc" : "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: nextDisabled ? "not-allowed" : "pointer", transition: `background ${transitions.fast}` }}>{nextLabel ?? "Next"}</button>}
      {onSave && <button type="button" onClick={onSave} disabled={saveDisabled} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: saveDisabled ? "#ccc" : "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: saveDisabled ? "not-allowed" : "pointer", transition: `background ${transitions.fast}` }}>{saveLabel ?? "Save"}</button>}
    </div>
  );
}

function SelectField({ label, value, onChange, placeholder, options, required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; options: { value: string; label: string }[]; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
        {label} {required && <span style={{ color: colors.primary }}>*</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: value ? "#111" : "#aaa", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function MiniBtn({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return <button onClick={onClick} disabled={disabled} style={{ width: "28px", height: "28px", borderRadius: "7px", border: "1px solid #e8e8e8", background: "#fff", color: disabled ? "#ddd" : "#555", fontFamily: typography.fontFamily, fontSize: typography.size.xs, cursor: disabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: disabled ? 0.5 : 1 }}>{label}</button>;
}
